import { GetScannedProductRes } from "gen/api/product/service_pb"
import { LedgerEntry_UomSnapshot } from "gen/ledger/models_pb"
import { Product } from "gen/product/models_pb"
import { UUID as PBUUID } from "gen/proto/uuid/models_pb"
import { Decimal } from "shared-ts/classes/decimal"
import { safeConvertQuantityToUoMQuantity } from "shared-ts/libs/product/unitsOfMeasure.helpers"
import { UnitOfMeasure } from "shared-ts/libs/product/unitsOfMeasure.types"

import {
  TransactionProduct,
  convertScanProductResToTransactionProduct,
} from "../transactionProduct"

type UomSelection = { uomId?: PBUUID; uomSnapshot?: LedgerEntry_UomSnapshot }

/**
 * Converts a barcode scan result into a TransactionProduct enriched with
 * validated UOM and converted quantity. Shared across Sales, Orders, and
 * Transfers — callers supply domain-specific strategy functions.
 */
export function enrichScanResultWithUom(args: {
  scanResult: GetScannedProductRes
  pricingRegionId: PBUUID | undefined
  showRegionPricing: boolean
  getDefaultUomId: (product?: Product) => PBUUID | undefined
  buildValidatedUomSelection: (args: {
    product?: Product
    uomId?: PBUUID
    companyUnitsOfMeasureById: Map<string, UnitOfMeasure>
  }) => UomSelection
  companyUnitsOfMeasureById: Map<string, UnitOfMeasure>
}): TransactionProduct {
  const {
    scanResult,
    pricingRegionId,
    showRegionPricing,
    getDefaultUomId,
    buildValidatedUomSelection,
    companyUnitsOfMeasureById,
  } = args

  let transactionProduct = convertScanProductResToTransactionProduct(
    scanResult,
    pricingRegionId,
    showRegionPricing
  )

  const uomId = scanResult.uomId ?? getDefaultUomId(scanResult.product)
  if (!uomId) return transactionProduct

  const selection = buildValidatedUomSelection({
    product: scanResult.product,
    uomId,
    companyUnitsOfMeasureById,
  })

  if (selection.uomId) {
    transactionProduct = {
      ...transactionProduct,
      quantity: safeConvertQuantityToUoMQuantity(
        transactionProduct.quantity,
        Decimal.fromPBOrZero(selection.uomSnapshot?.conversionFactorToBase)
      ),
      uomId: selection.uomId,
      uomSnapshot: selection.uomSnapshot,
    }
  }

  return transactionProduct
}
